/**
 * Global Search Pages List
 *
 * This file contains all searchable pages and settings for the GlobalSearch component.
 * When adding, removing, or modifying pages/settings:
 * - Update MoreRedesign.jsx if the page appears in settings
 * - Update More.jsx if the page appears in settings (non-redesign)
 * - Update App.jsx if the route needs to be added/modified
 * - Update NavRedesign.jsx if the page appears in navigation
 * - Ensure the route exists in App.jsx
 */

import { Roles } from '../../Data/Users';

const pages = [
    {
        title: 'Dashboard',
        to: '/',
        keywords: ['home', 'dashboard', 'main'],
    },
    {
        title: 'Work',
        to: '/work',
        keywords: ['work', 'schedule', 'today', 'diary', 'day'],
    },
    {
        title: 'Messages',
        to: '/messages',
        keywords: ['messages', 'sms', 'conversations', 'inbox'],
    },
    {
        title: 'Reports',
        to: '/reports',
        keywords: ['reports', 'analytics', 'statistics'],
    },
    {
        title: 'Report — Overview',
        to: '/reports/overview',
        keywords: ['reports', 'overview', 'summary'],
    },
    {
        title: 'Report — Jobs',
        to: '/reports/jobs',
        keywords: ['reports', 'jobs'],
    },
    {
        title: 'Report — Assigned work',
        to: '/reports/workers',
        keywords: ['reports', 'workers', 'assigned', 'staff'],
    },
    {
        title: 'Report — Invoices',
        to: '/reports/invoices',
        keywords: ['reports', 'invoices'],
    },
    {
        title: 'Report — Rounds',
        to: '/reports/rounds',
        keywords: ['reports', 'rounds'],
    },
    {
        title: 'Report — Expenses',
        to: '/reports/expenses',
        keywords: ['reports', 'expenses'],
    },
    {
        title: 'Report — Payments',
        to: '/reports/payments',
        keywords: ['reports', 'payments'],
    },
    {
        title: 'Report — Services',
        to: '/reports/services',
        keywords: ['reports', 'services'],
    },
    {
        title: 'Report — Profit and Loss',
        to: '/reports/profit-and-loss',
        keywords: ['reports', 'profit', 'loss', 'p&l'],
    },
    {
        title: 'Report — Forecast',
        to: '/reports/forecast',
        keywords: ['reports', 'forecast'],
    },
    {
        title: 'Report — Website',
        to: '/reports/website',
        keywords: ['reports', 'website'],
    },
    {
        title: 'Report — Recurring Revenue',
        to: '/reports/recurring-revenue',
        keywords: ['reports', 'recurring', 'revenue', 'mrr', 'service'],
    },
    {
        title: 'Report — Customer Growth',
        to: '/reports/customer-growth',
        keywords: ['reports', 'customers', 'growth', 'churn', 'acquisition', 'new customers'],
    },
    {
        title: 'Customers',
        to: '/manage/customers',
        keywords: ['customers', 'client', 'contacts'],
        feature: 'CUSTOMERS',
    },
    {
        title: 'Invoices',
        to: '/manage/invoices',
        keywords: ['invoices', 'billing'],
    },
    {
        title: 'Quotes',
        to: '/manage/quotes',
        keywords: ['quotes', 'estimates'],
    },
    {
        title: 'Payments',
        to: '/manage/payments',
        keywords: ['payments', 'money', 'transactions'],
    },
    {
        title: 'Expenses',
        to: '/manage/expenses',
        keywords: ['expenses', 'costs', 'spending'],
        feature: 'EXPENSES',
    },
    {
        title: 'Broadcasts',
        to: '/more/broadcasts',
        keywords: ['broadcasts', 'messages', 'bulk'],
        feature: 'BROADCASTS',
    },
    {
        title: 'Leads',
        to: '/plan/leads',
        keywords: ['leads', 'prospects'],
    },
    {
        title: 'Calendar',
        to: '/plan/calendar',
        keywords: ['calendar', 'schedule', 'appointments'],
        feature: 'WEEK_PLANNER',
    },
    {
        title: 'Rounds',
        to: '/plan/rounds',
        keywords: ['rounds', 'routes'],
        feature: 'ROUNDS',
    },
    {
        title: 'Jobs',
        to: '/plan/jobs',
        keywords: ['jobs', 'work', 'tasks'],
    },
    {
        title: 'Settings',
        to: '/more',
        keywords: ['settings', 'preferences', 'configuration'],
    },
    {
        title: 'Business Details',
        to: '/business/details',
        keywords: ['business', 'details', 'company', 'information'],
        role: Roles.Manager,
    },
    {
        title: 'Documents',
        to: '/business/documents',
        keywords: ['documents', 'files', 'templates'],
        feature: 'DOCUMENTS',
        role: Roles.Manager,
    },
    {
        title: 'Services',
        to: '/business/services',
        keywords: ['services', 'offerings'],
        role: Roles.Manager,
    },
    {
        title: 'Services & Pricing',
        to: '/business/services-and-pricing',
        keywords: ['services', 'pricing', 'combined'],
        role: Roles.Manager,
    },
    {
        title: 'Pricing',
        to: '/business/pricing',
        keywords: ['pricing', 'rates', 'prices'],
        role: Roles.Manager,
    },
    {
        title: 'Announcements',
        to: '/announcements',
        keywords: ['announcements', 'news', 'updates', 'customers'],
        feature: 'ANNOUNCEMENTS',
        role: Roles.Manager,
    },
    {
        title: 'Calendar feeds',
        to: '/settings/calendarFeeds',
        keywords: ['calendar', 'feeds', 'ical', 'integrations'],
        feature: 'CALENDAR_FEEDS',
        role: Roles.Owner,
    },
    {
        title: 'Calls',
        to: '/calls',
        keywords: ['calls', 'phone', 'voip', 'twilio'],
        adminOnly: true,
    },
    {
        title: 'Notifications',
        to: '/triggers',
        keywords: ['notifications', 'triggers', 'alerts', 'messages'],
        feature: 'CUSTOMER_NOTIFICATIONS',
        role: Roles.Manager,
    },
    {
        title: 'Customer Notifications',
        to: '/settings/customer-notifications',
        keywords: ['customer', 'notifications', 'alerts'],
        feature: 'CUSTOMER_NOTIFICATIONS',
        role: Roles.Manager,
    },
    {
        title: 'Message Templates',
        to: '/settings/message-templates',
        keywords: ['message', 'templates', 'sms', 'text'],
        feature: 'CUSTOMER_NOTIFICATIONS',
        role: Roles.Manager,
    },
    {
        title: 'Document Templates',
        to: '/settings/document-templates',
        keywords: ['document', 'templates', 'invoices'],
        feature: 'INVOICING',
        role: Roles.Manager,
    },
    {
        title: 'Custom SMS Name',
        to: '/business/custom-sms-name',
        keywords: ['sms', 'name', 'custom'],
        feature: 'SMS_CUSTOM_NAME',
        role: Roles.Manager,
    },
    {
        title: 'Setup Payments',
        to: '/settings/payments/setup',
        keywords: ['payments', 'setup', 'stripe', 'card'],
        feature: 'CARDS_PAYMENTS',
        role: Roles.Owner,
    },
    {
        title: 'Bank Account',
        to: '/settings/payments/bank-account',
        keywords: ['bank', 'account', 'payouts'],
        feature: 'CARDS_PAYMENTS',
        role: Roles.Owner,
    },
    {
        title: 'Payment Options',
        to: '/settings/payments/options',
        keywords: ['payment', 'options', 'methods'],
        feature: 'CARDS_PAYMENTS',
        role: Roles.Manager,
    },
    {
        title: 'Payouts',
        to: '/settings/payments/payouts',
        keywords: ['payouts', 'withdrawals', 'bank'],
        feature: 'CARDS_PAYMENTS',
        role: Roles.Owner,
    },
    {
        title: 'Bank Reconciliation',
        to: '/settings/payments/bank-reconciliation',
        keywords: ['bank', 'reconciliation', 'matching'],
        feature: 'BANK_RECONCILIATION',
        role: Roles.Manager,
    },
    {
        title: 'Making Tax Digital',
        to: '/hmrc',
        keywords: ['hmrc', 'mtd', 'tax', 'vat'],
        role: Roles.Owner,
    },
    {
        title: 'Links',
        to: '/website/links',
        keywords: ['links', 'website', 'urls'],
        role: Roles.Manager,
    },
    {
        title: 'Quote Options',
        to: '/settings/quote-options',
        keywords: ['quote', 'options', 'settings'],
        role: Roles.Manager,
    },
    {
        title: 'Website Type',
        to: '/website/type',
        keywords: ['website', 'type', 'setup'],
        role: Roles.Manager,
    },
    {
        title: 'Change Theme',
        to: '/website/theme',
        keywords: ['theme', 'website', 'design'],
        role: Roles.Manager,
    },
    {
        title: 'Update Content',
        to: '/website/content',
        keywords: ['content', 'website', 'update'],
        role: Roles.Manager,
    },
    {
        title: 'Custom Domain',
        to: '/website/custom-domain',
        keywords: ['domain', 'custom', 'website'],
        feature: 'CUSTOM_DOMAIN',
        role: Roles.Manager,
    },
    {
        title: 'Domain & Pages',
        to: '/website/third-party-settings',
        keywords: ['domain', 'pages', 'third', 'party'],
        role: Roles.Manager,
    },
    {
        title: 'Website Stats',
        to: '/website/stats',
        keywords: ['stats', 'website', 'analytics', 'traffic'],
        role: Roles.Manager,
    },
    {
        title: 'Builder',
        to: '/builder',
        keywords: ['builder', 'website', 'design'],
        adminOnly: true,
    },
    {
        title: 'Profile',
        to: '/user/profile',
        keywords: ['profile', 'account', 'user', 'name', 'email'],
    },
    {
        title: 'Security',
        to: '/user/security',
        keywords: ['security', 'password', 'change password'],
    },
    {
        title: 'App Notifications',
        to: '/user/notification-preferences',
        keywords: ['notifications', 'preferences', 'app'],
    },
    {
        title: 'Verification',
        to: '/verification',
        keywords: ['verification', 'verify', 'identity'],
        role: Roles.Owner,
    },
    {
        title: 'Close Account',
        to: '/user/close-account',
        keywords: ['close', 'account', 'delete'],
        role: Roles.Owner,
    },
    {
        title: 'Tags',
        to: '/settings/tags',
        keywords: ['tags', 'labels', 'categories'],
        feature: 'TAGS',
        role: Roles.Manager,
    },
    {
        title: 'Import',
        to: '/settings/import',
        keywords: ['import', 'data', 'upload'],
        role: Roles.Manager,
    },
    {
        title: 'API Keys',
        to: '/settings/integrations',
        keywords: ['api', 'integrations', 'oauth', 'keys', 'authorisations'],
        feature: 'API_ACCESS',
        role: Roles.Owner,
    },
    {
        title: 'Webhooks',
        to: '/settings/integrations/webhooks',
        keywords: ['webhooks', 'zapier', 'integrations', 'notifications'],
        feature: 'WEBHOOKS',
        role: Roles.Owner,
    },
    {
        title: 'Export',
        to: '/exports',
        keywords: ['export', 'download', 'data'],
        role: Roles.Manager,
    },
    {
        title: 'Reset Account',
        to: '/user/reset-account',
        keywords: ['reset', 'account', 'clear'],
        role: Roles.Owner,
    },
    {
        title: 'Additional Users',
        to: '/business/additional-users',
        keywords: ['users', 'team', 'members', 'additional'],
        feature: 'ADDITIONAL_USERS',
        role: Roles.Owner,
    },
    {
        title: 'Billing & Plan',
        to: '/billing',
        keywords: ['billing', 'plan', 'subscription', 'payment'],
        role: Roles.Owner,
    },
    {
        title: 'Billing Invoices',
        to: '/billing/invoices',
        keywords: ['billing', 'invoices', 'receipts', 'subscription'],
        role: Roles.Owner,
    },
    {
        title: 'Choose Plan',
        to: '/billing/plan',
        keywords: ['plan', 'billing', 'subscription', 'upgrade'],
        role: Roles.Owner,
    },
    {
        title: 'Help',
        to: '/help',
        keywords: ['help', 'support', 'faq'],
    },
    {
        title: 'Help Videos',
        to: '/help/videos',
        keywords: ['help', 'videos', 'tutorials'],
    },
    {
        title: 'Getting Started Checklist',
        to: '/help/checklist',
        keywords: ['checklist', 'getting', 'started', 'guide'],
    },
    {
        title: 'Documentation',
        to: 'https://getsoapy.com/docs',
        keywords: ['documentation', 'docs', 'help'],
        external: true,
    },
    {
        title: 'What\'s New',
        to: '/whats-new',
        keywords: ['whats', 'new', 'updates', 'changelog'],
    },
    {
        title: 'Feature Suggestions',
        to: '/feature-suggestions',
        keywords: ['feature', 'suggestions', 'roadmap', 'feedback', 'ideas'],
    },
    {
        title: 'Follow us on Social Media',
        to: '/get-social',
        keywords: ['social', 'media', 'follow'],
    },
    {
        title: 'Refer a Friend',
        to: '/refer-a-friend',
        keywords: ['refer', 'friend', 'referral'],
        role: Roles.Owner,
    },
    {
        title: 'Communication Preferences',
        to: '/more/communication-preferences',
        keywords: ['communication', 'preferences', 'email'],
    },
    {
        title: 'Experimental Features',
        to: '/more/experimental',
        keywords: ['experimental', 'features', 'beta'],
    },
];

export default pages;
